import { useState, useCallback } from 'react'
import { MagnifyingGlass, CircleNotch } from '@phosphor-icons/react'
import { lookupCNPJ, formatCNPJ, isValidCNPJFormat, type CNPJData } from '../../services/brasilapi'
import { CNPJPreviewModal } from './CNPJPreviewModal'
import { useDialog } from '../../context/DialogContext'

interface CNPJLookupInputProps {
  value: string
  onChange: (value: string) => void
  onDataConfirmed?: (data: CNPJData) => void
  disabled?: boolean
  error?: string
  label?: string
  placeholder?: string
  required?: boolean
  className?: string
}

export function CNPJLookupInput({
  value,
  onChange,
  onDataConfirmed,
  disabled = false,
  error,
  label = 'CNPJ',
  placeholder = '00.000.000/0000-00',
  required = false,
  className = '',
}: CNPJLookupInputProps) {
  const { toastError } = useDialog()

  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [cnpjData, setCnpjData] = useState<CNPJData | null>(null)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, '')
    const formatted = formatCNPJ(rawValue)
    onChange(formatted)
  }

  const handleLookup = useCallback(async () => {
    const cleanCNPJ = value.replace(/\D/g, '')

    if (!cleanCNPJ) {
      toastError('CNPJ Inválido', 'Digite um CNPJ para buscar')
      return
    }

    if (!isValidCNPJFormat(cleanCNPJ)) {
      toastError('CNPJ Inválido', 'O CNPJ deve ter 14 dígitos')
      return
    }

    setLoading(true)

    try {
      const data = await lookupCNPJ(cleanCNPJ)
      setCnpjData(data)
      setModalOpen(true)
    } catch (err: any) {
      const message = err.message || 'Erro ao buscar dados do CNPJ'
      toastError('Erro na Busca', message)
    } finally {
      setLoading(false)
    }
  }, [value, toastError])

  const handleConfirm = (data: CNPJData) => {
    setModalOpen(false)
    setCnpjData(null)
    if (onDataConfirmed) {
      onDataConfirmed(data)
    }
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setCnpjData(null)
  }

  const canSearch = value.replace(/\D/g, '').length === 14 && !disabled && !loading

  return (
    <>
      <div className={className}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              value={value}
              onChange={handleInputChange}
              placeholder={placeholder}
              disabled={disabled}
              maxLength={18}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors ${
                error
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-200'
              } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            />
          </div>
          <button
            type="button"
            onClick={handleLookup}
            disabled={!canSearch}
            className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
              canSearch
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            title={canSearch ? 'Buscar dados do CNPJ' : 'Digite um CNPJ válido para buscar'}
          >
            {loading ? (
              <CircleNotch size={20} className="animate-spin" />
            ) : (
              <MagnifyingGlass size={20} />
            )}
            <span className="hidden sm:inline">Buscar</span>
          </button>
        </div>
        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        <p className="mt-1 text-xs text-gray-500">
          Busque os dados da empresa automaticamente pela API da Receita Federal
        </p>
      </div>

      <CNPJPreviewModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
        data={cnpjData}
        loading={loading}
      />
    </>
  )
}
